import React from 'react'
import {
  PiArrowLeft,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiToolbox,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { ComposeMode } from './ComposePanel'
import {
  EmailDealActionCallbacks,
  buildActionContext,
  useEmailThreadActions,
} from './emailActions'
import { EmailActionMenuItems } from './EmailActionMenuItems'

interface EmailDetailActionBarProps extends EmailDealActionCallbacks {
  threadIndex: number
  totalThreads: number
  pageSize: number
  pageStart: number
  onBack: () => void
  onNavigateThread: (delta: number) => void
  onCompose?: (mode: ComposeMode) => void
  assignDealSubContent?: React.ReactNode
  isHidden?: boolean
}

/** Action bar for the email detail view with back button, actions dropdown, and pagination. */
export function EmailDetailActionBar({
  threadIndex,
  totalThreads,
  pageSize,
  pageStart,
  onBack,
  onNavigateThread,
  onCompose,
  assignDealSubContent,
  isHidden,
  ...dealCallbacks
}: EmailDetailActionBarProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const actions = useEmailThreadActions(
    buildActionContext(dealCallbacks, onCompose, assignDealSubContent, isHidden)
  )

  const pageEnd = Math.min(pageStart + pageSize - 1, totalThreads)
  const hasPrev = threadIndex > 0
  const hasNext = threadIndex < pageEnd - pageStart

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center size-8 rounded hover:bg-surface-alt active:bg-surface-alt text-fg-secondary hover:text-fg transition-colors"
          title="Back to list"
        >
          <PiArrowLeft className="size-5" />
        </button>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-8 px-1.5 rounded hover:bg-surface-alt active:bg-surface-alt text-sm font-medium text-fg-secondary hover:text-fg transition-colors"
            >
              <PiToolbox className="size-6" />
              <span>Actions</span>
              <PiCaretDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            style={{ minWidth: 200, zIndex }}
            className="bg-surface border-line-focus"
          >
            <EmailActionMenuItems actions={actions} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-3 text-sm text-fg-secondary">
        <span>
          {totalThreads > 0 ? `${pageStart}\u2013${pageEnd}` : '0'} of{' '}
          {totalThreads.toLocaleString()}
        </span>
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onNavigateThread(-1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasPrev
              ? 'hover:bg-black/5 text-fg-secondary hover:text-fg'
              : 'text-fg-muted cursor-not-allowed'
          )}
        >
          <PiCaretLeft className="size-[22px]" />
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onNavigateThread(1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasNext
              ? 'hover:bg-black/5 text-fg-secondary hover:text-fg'
              : 'text-fg-muted cursor-not-allowed'
          )}
        >
          <PiCaretRight className="size-[22px]" />
        </button>
      </div>
    </div>
  )
}
